import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Moon, Sun, Bell, User, Menu, LogOut, Loader } from "lucide-react";
import { useTheme } from "../../../context/ThemeContext";
import DASImageLight from "../../../assets/das_logo_light.png";
import DASImageDark from "../../../assets/das_logo_dark.png";
import TimezoneSelector from "../../common/TimezoneSelector/TimezoneSelector";
import "./Navbar.css";

// Redux actions
import { logout } from "../../../store/slices/authSlice";
import { fetchStats } from "../../../store/slices/statsSlice";

const Navbar = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const dropdownRef = useRef(null);

  const { isDark, toggleTheme } = useTheme();

  // Get user info from Redux
  const { username, role } = useSelector((state) => state.auth);
  const globalPostureScore = useSelector(
    (state) => state.stats?.data?.risk_stats?.score?.latest_percentage,
  );

  useEffect(() => {
    dispatch(fetchStats());
  }, [dispatch]);
  // console.log('global.......', globalPostureScore)

  // Fallback to localStorage if Redux doesn't have the data yet
  const userObj = JSON.parse(localStorage.getItem("currentUser") || "{}");
  const currentUserName = username || userObj.userName || "Guest User";
  const currentUserRole = role || userObj?.role;

  // Toggle dropdown visibility
  const toggleDropdown = () => setIsDropdownOpen(!isDropdownOpen);

  const handleLogout = async () => {
    try {
      setIsLoggingOut(true);

      // Dispatch Redux logout action - This clears all state via root reducer
      dispatch(logout());

      // Close dropdown
      setIsDropdownOpen(false);

      // Small delay for smooth transition
      await new Promise((resolve) => setTimeout(resolve, 300));

      // Redirect to login page
      navigate("/login", { replace: true });
    } catch (error) {
      console.error("Logout error:", error);
    } finally {
      setIsLoggingOut(false);
    }
  };

  const handleUserAccess = () => {
    setIsDropdownOpen(false);
    navigate("/user-access-management"); // make sure this route exists
  };

  // Close dropdown if clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <header className="navbar">
      {/* ── Left Section ───────────────────────────── */}
      <div className="navbar-left">
        {/* <button
          className="navbar-hamburger"
          onClick={onMenuClick}
          aria-label="Toggle navigation menu"
        >
          <Menu size={22} />
        </button> */}

        <div className="navbar-logo">
          <img src={isDark ? DASImageDark : DASImageLight} alt="DAS Logo" />
        </div>
      </div>

      {/* ── Right Section ──────────────────────────── */}
      <div className="navbar-right">
        {/* Theme Toggle */}
        {/* <Moon className="navbar-icon" aria-label="Toggle theme" /> */}

        {/* Notifications */}
        {/* <div className="navbar-notification">
          <Bell className="navbar-icon" aria-label="Notifications" />
          <span className="notification-badge" aria-label="12 notifications">
            12
          </span>
        </div> */}
        <div className="navbar-right-inner">
        <TimezoneSelector />

        {/* Global Posture Score */}
        <div className="navbar-posture">
          <span className="posture-dot" aria-hidden="true" />
          <span className="posture-label">Global Posture</span>
          <span className="posture-score">
            {globalPostureScore ? Math.round(globalPostureScore) : "--"}
          </span>
          <span className="posture-total">/100</span>
        </div>
        </div>


        <button
          className="navbar-icon-btn"
          onClick={toggleTheme}
          aria-label="Toggle theme"
          title={isDark ? "Switch to light" : "Switch to dark"}
        >
          {isDark ? <Sun size={20} /> : <Moon size={20} />}
        </button>
        {/* ── User Profile & Dropdown ────────────────── */}
        <div className="navbar-user-container" ref={dropdownRef}>
          <User
            className="navbar-user-icon"
            aria-label="User profile"
            onClick={toggleDropdown}
          />

          {isDropdownOpen && (
            <div className="navbar-dropdown">
              <div className="dropdown-header">
                {/* Displays: abc@example.com */}
                <p className="user-name">{currentUserName}</p>
              </div>
              {(currentUserRole === "super_admin" ||
                currentUserRole === "admin") && (
                <button onClick={handleUserAccess} className="dropdown-item">
                  <span>User Access Management</span>
                </button>
              )}

              <button
                onClick={handleLogout}
                className="dropdown-item logout-btn"
              >
                <LogOut size={16} />
                <span>Logout</span>
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default Navbar;
